// Phase 2 — non-flower variants (apparel sizes + concentrate weights).
// Mirrors backend `VARIANT_TEMPLATES`.
//
// Schema (on product):
//   variants:        [{key, label, price?}]
//   variant_stock:   { variant_key: { region: stock } }
//   region_prices.<region>.variant_prices: { variant_key: price }  (optional override)
//
// Flower keeps its own ladder (`utils/flowerSizes.js`) — variants[] is only used
// for apparel + concentrates today.

export const VARIANT_TEMPLATES = {
  apparel: [
    { key: 'XS',  label: 'XS' },
    { key: 'S',   label: 'S' },
    { key: 'M',   label: 'M' },
    { key: 'L',   label: 'L' },
    { key: 'XL',  label: 'XL' },
    { key: 'XXL', label: 'XXL' },
  ],
  concentrates: [
    { key: '0.5g', label: '0.5g', price: 25 },
    { key: '1g',   label: '1g',   price: 45 },
    { key: '2g',   label: '2g',   price: 80 },
  ],
  // Phase 6.0 — Preroll category (single, half-eighth pack, eighth pack).
  preroll: [
    { key: '1g',   label: '1g',   price: 15 },
    { key: '3.5g', label: '3.5g', price: 40 },
    { key: '5g',   label: '5g',   price: 55 },
  ],
};

export function hasVariants(product) {
  return Array.isArray(product?.variants) && product.variants.length > 0;
}

export function getVariants(product) {
  return Array.isArray(product?.variants) ? product.variants : [];
}

// Which template (if any) should auto-seed for this product?
// Returns a template key ('apparel' | 'concentrates' | 'preroll') or null.
export function suggestedVariantTemplate(product) {
  const cat = String(product?.category || '').toLowerCase();
  const sub = String(product?.subcategory || '').toLowerCase();
  if (cat === 'concentrates') return 'concentrates';
  if (cat === 'preroll') return 'preroll';
  if (cat === 'merch' && sub === 'apparel') return 'apparel';
  return null;
}

// True for product categories where the Edit modal should show the variant editor.
export function supportsVariants(category, subcategory) {
  const cat = String(category || '').toLowerCase();
  const sub = String(subcategory || '').toLowerCase();
  if (cat === 'concentrates') return true;
  if (cat === 'preroll') return true;
  if (cat === 'merch' && sub === 'apparel') return true;
  return false;
}

// Resolve a variant's unit price for a given region, applying region overrides.
export function variantPriceFor(product, key, region = null) {
  const rp = (product?.region_prices || {})[region] || {};
  const vp = rp.variant_prices || {};
  const custom = vp[key];
  if (typeof custom === 'number' && custom > 0) return custom;
  const v = (product?.variants || []).find((x) => x.key === key);
  if (v && typeof v.price === 'number' && v.price > 0) return v.price;
  const rpBase = rp.base;
  if (typeof rpBase === 'number' && rpBase > 0) return rpBase;
  return Number(product?.price || 0);
}

// Per-variant per-region stock. Falls back to 0 when missing.
export function variantStockFor(product, key, region) {
  const vs = product?.variant_stock || {};
  const byRegion = vs[key] || {};
  if (region) return Number(byRegion[region] || 0);
  return Object.values(byRegion).reduce((s, n) => s + Number(n || 0), 0);
}

// Friendly label for the variant axis ("SIZE" / "WEIGHT").
export function variantAxisLabel(product) {
  const cat = String(product?.category || '').toLowerCase();
  if (cat === 'concentrates') return 'WEIGHT';
  return 'SIZE';
}
